import axiosClient from "utils/axios";
import { ApiOptionList, ApiResponse } from "../../../types/api.types";
import {
  CreateProductCategoryPayload,
  ProductCategoriesTableResponse,
  ProductCategory,
  UpdateProductCategoryPayload,
} from "../types/product-category.types";

function normalizeListResponse(
  raw: ProductCategory[] | ProductCategoriesTableResponse,
  page: number,
  limit: number,
  search?: string,
): ProductCategoriesTableResponse {
  let rows = Array.isArray(raw) ? raw : raw.rows;

  if (search?.trim()) {
    const q = search.trim().toLowerCase();
    rows = rows.filter((r) => r.name?.toLowerCase().includes(q));
  }

  const totalItems = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const start = (page - 1) * limit;
  const pageRows = rows.slice(start, start + limit);

  return {
    rows: pageRows,
    totalItems,
    currentPage: page,
    pageSize: limit,
    limit,
    totalPages,
  };
}

export const getProductCategories = async ({
  page = 1,
  limit = 10,
  search,
}: {
  page?: number;
  limit?: number;
  search?: string;
} = {}) => {
  try {
    const response = await axiosClient.get<
      ApiResponse<
        ProductCategoriesTableResponse | { rows: ProductCategory[] }
      >
    >("/product-categories", {
      params: {
        query: search || undefined,
        page,
        limit,
      },
    });

    const payload = response.data.data;

    if (!payload) {
      const normalized = normalizeListResponse([], page, limit, search);
      return {
        success: response.data.success,
        message: response.data.message,
        data: normalized,
      } satisfies ApiResponse<ProductCategoriesTableResponse>;
    }

    if (
      payload &&
      typeof payload === "object" &&
      "totalItems" in payload &&
      Array.isArray(payload.rows)
    ) {
      return response.data as ApiResponse<ProductCategoriesTableResponse>;
    }

    const rows = (payload as { rows?: ProductCategory[] })?.rows ?? [];
    const normalized = normalizeListResponse(rows, page, limit, search);

    return {
      success: response.data.success,
      message: response.data.message,
      data: normalized,
    } satisfies ApiResponse<ProductCategoriesTableResponse>;
  } catch (err) {
    console.warn("Error fetching product categories: ", err);
    throw err;
  }
};

export const getProductCategoryOptions = async ({
  query,
  limit = 20,
  page = 1,
}: {
  query?: string;
  limit?: number;
  page?: number;
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      "/product-categories/options",
      {
        params: {
          query,
          limit,
          page,
        },
      },
    );

    return response.data;
  } catch (err) {
    console.warn("Error fetching product category options: ", err);
    throw err;
  }
};

export const getProductCategory = async (id: number) => {
  try {
    const response = await axiosClient.get<ApiResponse<ProductCategory>>(
      `/product-categories/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching product category: ", err);
    throw err;
  }
};

export const createProductCategory = async (
  payload: CreateProductCategoryPayload,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<ProductCategory>>(
      "/product-categories",
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating product category: ", err);
    throw err;
  }
};

export const updateProductCategory = async (
  id: number,
  payload: UpdateProductCategoryPayload,
) => {
  try {
    const response = await axiosClient.put<ApiResponse<ProductCategory>>(
      `/product-categories/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating product category: ", err);
    throw err;
  }
};

export const deleteProductCategory = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<ProductCategory>>(
      `/product-categories/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting product category: ", err);
    throw err;
  }
};
